"use client";

import { c as _c } from "react/compiler-runtime";
import { SkeletonLoader } from "@/components/ui/skeleton";
import useContainerWidth from "@/hooks/use-container-width";
import { useNearbySUPs } from "@/hooks/use-nearby-sups";
import { usePropertyInteractionStore } from "@/stores/property-interaction-store";
import { type NearbyProperty } from "@/types/sup";
import { useState } from "react";
import { SUPCard } from "../sup/card";
import { NextButton, PreviousButton } from "./pagination-buttons";
import { ProjectCard } from "./sp-card";
interface NearbySUPsClientProps {
  coordinates: {
    lat: number;
    lng: number;
  };
  initialData?: NearbyProperty[];
  loading?: boolean;
  distanceText?: string;
  type: "unit" | "sp";
  excludeId?: string;
  shouldPrioritizeCoverImage?: boolean; // Add prop to control image prioritization
  isNotAvailableForSale?: boolean;
  shouldRedirect?: boolean;
  unitId?: string; // Optional: enables ranked/filtered results
}
export function NearbySUPsClient(t0) {
  const $ = _c(59);
  const {
    coordinates,
    initialData: t1,
    loading: t2,
    distanceText,
    type,
    excludeId,
    shouldPrioritizeCoverImage: t3,
    isNotAvailableForSale: t4,
    shouldRedirect,
    unitId
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const initialData = t5;
  const parentLoading = t2 === undefined ? false : t2;
  const shouldPrioritizeCoverImage = t3 === undefined ? false : t3;
  const isNotAvailableForSale = t4 === undefined ? false : t4;
  let t6;
  if ($[2] !== coordinates || $[3] !== excludeId || $[4] !== initialData || $[5] !== type || $[6] !== unitId) {
    t6 = {
      coordinates,
      type,
      unitId,
      initialData,
      initialError: null,
      excludeId
    };
    $[2] = coordinates;
    $[3] = excludeId;
    $[4] = initialData;
    $[5] = type;
    $[6] = unitId;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const {
    data: sups,
    error,
    hasMore,
    hasPrevious,
    loadingMore,
    loadingPrevious,
    loadMore,
    loadPrevious,
    retry
  } = useNearbySUPs(t6);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const {
    containerRef,
    containerWidth
  } = useContainerWidth();
  const columns = containerWidth < 512 ? 1 : containerWidth < 896 ? 2 : 3;
  const rowsPerPage = isNotAvailableForSale ? 2 : 1;
  const itemsPerPage = columns * rowsPerPage;
  const openPropertyOverlay = usePropertyInteractionStore(_temp);
  const totalPages = Math.ceil((sups?.length || 0) / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  let t10;
  let t7;
  let t8;
  let t9;
  if ($[8] !== containerRef || $[9] !== currentPage || $[10] !== distanceText || $[11] !== endIndex || $[12] !== error || $[13] !== hasMore || $[14] !== hasPrevious || $[15] !== isLoading || $[16] !== loadMore || $[17] !== loadPrevious || $[18] !== loadingMore || $[19] !== loadingPrevious || $[20] !== openPropertyOverlay || $[21] !== parentLoading || $[22] !== retry || $[23] !== shouldPrioritizeCoverImage || $[24] !== shouldRedirect || $[25] !== startIndex || $[26] !== sups || $[27] !== totalPages || $[28] !== type) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const currentItems = sups?.slice(startIndex, endIndex) || [];
      let t11;
      if ($[33] !== currentPage || $[34] !== hasMore || $[35] !== loadMore || $[36] !== loadingMore || $[37] !== totalPages) {
        t11 = async () => {
          if (currentPage < totalPages - 1) {
            setCurrentPage(currentPage + 1);
          } else {
            if (hasMore && !loadingMore) {
              setIsLoading(true);
              await loadMore();
              setIsLoading(false);
            }
          }
        };
        $[33] = currentPage;
        $[34] = hasMore;
        $[35] = loadMore;
        $[36] = loadingMore;
        $[37] = totalPages;
        $[38] = t11;
      } else {
        t11 = $[38];
      }
      const handleNextPage = t11;
      let t12;
      if ($[39] !== currentPage || $[40] !== hasPrevious || $[41] !== loadPrevious || $[42] !== loadingPrevious) {
        t12 = async () => {
          if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
          } else {
            if (hasPrevious && !loadingPrevious) {
              setIsLoading(true);
              await loadPrevious();
              setIsLoading(false);
            }
          }
        };
        $[39] = currentPage;
        $[40] = hasPrevious;
        $[41] = loadPrevious;
        $[42] = loadingPrevious;
        $[43] = t12;
      } else {
        t12 = $[43];
      }
      const handlePrevPage = t12;
      if (parentLoading) {
        let t13;
        if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
          t13 = <div className="py-2.5 border-b border-primary/20"><SkeletonLoader height="20px" width="120px" /></div>;
          $[44] = t13;
        } else {
          t13 = $[44];
        }
        let t14;
        if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
          t14 = <div className="grid grid-cols-1 @lg:hidden gap-4">{Array.from({
              length: 1
            }).map(_temp2)}</div>;
          $[45] = t14;
        } else {
          t14 = $[45];
        }
        let t15;
        if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
          t15 = <div className="hidden @lg:grid-cols-2 @4xl:hidden gap-4">{Array.from({
              length: 2
            }).map(_temp3)}</div>;
          $[46] = t15;
        } else {
          t15 = $[46];
        }
        let t16;
        if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
          t16 = <div className="hidden @lg:grid-cols-2 @4xl:grid-cols-3 gap-4">{Array.from({
              length: 3
            }).map(_temp4)}</div>;
          $[47] = t16;
        } else {
          t16 = $[47];
        }
        let t17;
        if ($[48] !== t14 || $[49] !== t15 || $[50] !== t16) {
          t17 = <div>{t13}<div className="mt-3">{t14}{t15}{t16}</div></div>;
          $[48] = t14;
          $[49] = t15;
          $[50] = t16;
          $[51] = t17;
        } else {
          t17 = $[51];
        }
        t10 = t17;
        break bb0;
      }
      t7 = containerRef;
      if ($[52] !== error || $[53] !== retry) {
        t8 = error && <div className="text-red-500 text-sm mt-1">{error} -{" "}<button type="button" onClick={retry} className="underline hover:text-red-600">Try again</button></div>;
        $[52] = error;
        $[53] = retry;
        $[54] = t8;
      } else {
        t8 = $[54];
      }
      t9 = sups && sups.length > 0 ? <div className="mt-3 relative">{isLoading && <div className="absolute top-1/2 right-4 transform -translate-y-1/2 z-20 bg-white/80 rounded-full p-2 shadow-md"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}<div className="flex justify-between items-center mb-4"><div className="text-sm text-gray-600"><span className="sm:hidden">Showing{" "}{startIndex + 1 === Math.min(endIndex, sups.length) ? `${startIndex + 1} of ${sups.length} ` : `${startIndex + 1}-${Math.min(endIndex, sups.length)} of ${sups.length} `}{type === "sp" ? "projects" : "units"}</span><span className="hidden sm:inline">Showing {startIndex + 1}-{Math.min(endIndex, sups.length)} of {sups.length}{" "}{type === "sp" ? "projects" : "units"}</span></div><div className="flex gap-2"><PreviousButton hasPrevious={currentPage > 0 || hasPrevious} isLoading={isLoading || loadingPrevious} loadPrevious={handlePrevPage} /><NextButton hasMore={currentPage < totalPages - 1 || hasMore} isLoading={isLoading || loadingMore} loadMore={handleNextPage} /></div></div><div className="grid grid-cols-1 @lg:grid-cols-2 @4xl:grid-cols-3 gap-4 mb-6">{currentItems.map((sup, index_2) => <div key={`${sup._id}-${index_2}`} className="cursor-pointer transition-all duration-300 hover:shadow-xl rounded-2xl overflow-hidden" onClick={() => {
            if (shouldRedirect) {
              return;
            }
            openPropertyOverlay({
              sups: [sup],
              initialSupId: sup._id,
              projectName: sup.projectName
            });
          }}><div className="overflow-hidden rounded-2xl">{type === "sp" ? <ProjectCard data={sup} distanceFromText={distanceText} shouldPrioritizeCoverImage={shouldPrioritizeCoverImage} shouldRedirect={shouldRedirect} /> : <SUPCard data={sup} distanceFromText={distanceText} shouldPrioritizeCoverImage={shouldPrioritizeCoverImage} shouldRedirect={shouldRedirect} />}</div></div>)}</div></div> : !loadingMore ? <div className="mt-3 text-center py-8 text-gray-500"><p>No nearby {type === "sp" ? "projects" : "units"} found</p></div> : null;
    }
    $[8] = containerRef;
    $[9] = currentPage;
    $[10] = distanceText;
    $[11] = endIndex;
    $[12] = error;
    $[13] = hasMore;
    $[14] = hasPrevious;
    $[15] = isLoading;
    $[16] = loadMore;
    $[17] = loadPrevious;
    $[18] = loadingMore;
    $[19] = loadingPrevious;
    $[20] = openPropertyOverlay;
    $[21] = parentLoading;
    $[22] = retry;
    $[23] = shouldPrioritizeCoverImage;
    $[24] = shouldRedirect;
    $[25] = startIndex;
    $[26] = sups;
    $[27] = totalPages;
    $[28] = type;
    $[29] = t10;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t10 = $[29];
    t7 = $[30];
    t8 = $[31];
    t9 = $[32];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[55] !== t7 || $[56] !== t8 || $[57] !== t9) {
    t11 = <div ref={t7}>{t8}{t9}</div>;
    $[55] = t7;
    $[56] = t8;
    $[57] = t9;
    $[58] = t11;
  } else {
    t11 = $[58];
  }
  return t11;
}
function _temp4(__1, index_1) {
  return <div key={index_1}><SkeletonLoader height="200px" className="rounded-lg mb-4" /><SkeletonLoader height="16px" width="80%" className="mb-2" /><SkeletonLoader height="14px" width="60%" /></div>;
}
function _temp3(__0, index_0) {
  return <div key={index_0}><SkeletonLoader height="200px" className="rounded-lg mb-4" /><SkeletonLoader height="16px" width="80%" className="mb-2" /><SkeletonLoader height="14px" width="60%" /></div>;
}
function _temp2(_, index) {
  return <div key={index}><SkeletonLoader height="200px" className="rounded-lg mb-4" /><SkeletonLoader height="16px" width="80%" className="mb-2" /><SkeletonLoader height="14px" width="60%" /></div>;
}
function _temp(state) {
  return state.openPropertyOverlay;
}